import * as React from 'react';
import Script from 'react-load-script';
import Axios, {AxiosResponse} from 'axios';
import * as ReactTooltip from 'react-tooltip';
import * as _ from 'lodash';

import {LogoEditorGallery} from './logo-editor-gallery.component';
import {withAppContext} from '../../shared/app-context';
import {langChangedName, localization, LocHelperInstance} from '../../shared/localization/localization';
import {
    LogoEditorImage,
    LogoEditorImageControls,
    LogoEditorObjectSettings,
    LogoEditorPosition,
    LogoEditorPreview
} from './logo-editor-controls';
import {
    IBrandingTechnology,
    ICreateMotiveRequest,
    ICreateMotiveResponse,
    ICustomEditorButtonsSettings,
    IInitEditorRequest,
    IInitEditorResponse,
    ILogoEditorContainerProps,
    ILogoEditorContainerState,
    ILogoImageResponse,
    ILogoVariantsInfo,
    IMotiveInfo,
    IPdfPreviewRequest,
    IProductImageResponse,
    IProductVisualization,
    IProductVisualizationData,
    IProofreadingRequest,
    IProofreadingResponse,
    SaveMotiveResult
} from './tron-editor-interfaces';
import {LogoEditorPreviewControls} from './logo-editor-controls/logo-editor-preview-controls.component';
import {
    IPrintData,
    ISize,
    ITronEditor,
    ITronEditorParams,
    IVisualizationModeParam,
    LogoColorVariant,
    PrintEffectType,
    ShapeType
} from './tron-editor-api';
import {SavingMotiveDialog} from './logo-editor-dialogs/saving-motive.dialog';
import {DebuggerInstance} from 'src/App/shared/debugger';
import SimpleMessageDialog from 'src/App/shared/components/simple-message.dialog';
import {SaveMotiveErrorDialog} from './logo-editor-dialogs/save-motive-error.dialog';
import {ISimpleResponse} from 'src/App/shared/common-dtos';
import {Proofreading} from './logo-editor-dialogs/proofreading.dialog';
import {GeneratingPdfDialog} from './logo-editor-dialogs/generating-pdf.dialog';
import {PreviewSendSuccessDialog} from './logo-editor-dialogs/preview-send-success.dialog';
import {SendProofreadingErrorDialog} from './logo-editor-dialogs/send-proofreading-error.dialog';
import {Markdown} from 'src/App/shared/localization/markdown-wrapper';
import SimpleLoadingDialog from 'src/App/shared/components/simple-loading.dialog';
import {EditorLoadErrorDialog} from './logo-editor-dialogs/editor-load-error.dialog.';
import {LogoUploadEditor} from './logo-editor-upload.component';
import {SimpleChoiceDialog} from 'src/App/shared/components/simple-choice.dialog';
import {SaveMotiveDialog} from './logo-editor-dialogs/save-motive.dialog';
import {CustomLogoEditorPreviewControls} from "./logo-editor-controls/custom-preview-controls.component";
import {EditorButtonsFunctions} from "../../shared/logo.types";

export type LogoVariantDic = {[logoId: string]: ILogoVariantsInfo}

declare var promotron: any; // namespace of tron editor
const maxMotiveColors = 8;
const closeDelay = 1000;

export class LogoEditorContainer extends React.Component<ILogoEditorContainerProps, ILogoEditorContainerState> {
    private readonly debouncePrintZoneSizeChanged: ((fn: any) => void) & _.Cancelable;
    private langUpdateHandle = () => {
        let localizedTitle = localization.editorLoc.title;
        if (this.state.productTitle) {
            localizedTitle += ": " + (this.state.sName || this.state.productTitle);
        }
        this.props.appContext.setTitle(localizedTitle);
        this.loadCustomEditorButtons().then();
    };

    private editor!: ITronEditor;
    private readonly apiUrl: string;
    private editorSize: ISize = { width: 0, height: 0};
    private visualizationSize: ISize = { width: 0, height: 0};

    public static getDerivedStateFromProps(props: ILogoEditorContainerProps, state: ILogoEditorContainerState): Partial<ILogoEditorContainerState> | null {
        DebuggerInstance.log('getDerivedStateFromProps');
        DebuggerInstance.log(props);
        DebuggerInstance.log(state);

        if (state.editorDataInitialized) {
            return null;
        }

        const query = new URLSearchParams(props.location.search);
        const globalLogoId = props.appContext.getLogoId();
        let logoId = query.get('logooverrider');
        let logoIsOverride = true;
        if (!logoId) {
            logoId = globalLogoId;
            logoIsOverride = false;
        }

        const productMotiveId = query.get('pmotive');
        const effectCode = query.get('effectcode');

        let initialColorVariant: LogoColorVariant | undefined;
        const colorModeParam = query.get('viscolormode');
        if (colorModeParam) {
            switch (colorModeParam) {
                case 'original':
                    initialColorVariant = LogoColorVariant.Original;
                    break;
                case 'black':
                    initialColorVariant = LogoColorVariant.Black;
                    break;
                case 'white':
                    initialColorVariant = LogoColorVariant.White;
                    break;
            }
        }

        const stateUpdate: Partial<ILogoEditorContainerState> = {
            pPrefix: query.get('prefix') || '',
            pCode: query.get('pcode'),
            pGroup: query.get('pgroup'),
            pVariant: query.get('pvariant'),
            sCode: query.get('scode'),
            sName: query.get('sname'),
            logoId,
            logoIsOverride,
            globalLogoId,
            initialColorVariant,
            productMotiveId,
            presetEffectCode: effectCode || undefined
        };

        DebuggerInstance.log('getDerivedStateFromProps + stateUpdate');
        DebuggerInstance.log(stateUpdate);

        return stateUpdate;
    }

    constructor(props: ILogoEditorContainerProps) {
        super(props);

        this.props.appContext.setPopupLock(true);
        const userId = props.appContext.getUserId();
        const apiKey = props.appContext.getApiKey();
        this.apiUrl = `/api/${apiKey}/user/${userId}/tron-editor/`;

        this.debouncePrintZoneSizeChanged = _.debounce((fn) => {fn()}, 250);

        const defaultState: ILogoEditorContainerState = {
            pPrefix: '',
            pCode: null,
            pGroup: null,
            pVariant: null,
            sCode: null,
            sName: null,
            logoId: null,
            logoIsMotive: false,
            logoMotiveData: null,
            logoIsOverride: false,
            globalLogoId: null,
            productMotiveId: null,
            scriptLoaded: false,
            editorInitialized: false,
            editorDataInitialized: false,
            editorLoaded: false,
            selectedObjectId: undefined,
            productId: null,
            productSupplierId: null,
            productTitle: null,
            magnetEnabled: false,
            printData: {
                areaWidth: 0,
                areaHeight: 0,
                motiveWidth: 0, 
                motiveHeight: 0
            } as IPrintData,
            logoVariantCache: {},
            canvasColorsCount: 0,
            initialLogoColors: [],
            brandingTechnologies: [],
            selectedBrandingTechnology: null,
        };
        const stateFromProps = LogoEditorContainer.getDerivedStateFromProps(props, defaultState);
        this.state = Object.assign(defaultState, stateFromProps);

        DebuggerInstance.log('LogoEditorContainerComponent state:');
        DebuggerInstance.log(this.state);

        this.updatePrintZoneSize = this.updatePrintZoneSize.bind(this);
    }

    public async componentDidMount() {
        DebuggerInstance.log('LogoEditorContainerComponent-componentDidMount');

        LocHelperInstance.langChanged.addListener(langChangedName, this.langUpdateHandle);
        this.langUpdateHandle();

        // editor loading overlay
        this.props.appContext.openDialog(
            <SimpleLoadingDialog
                title={localization.editorLoc.logoEditorContent.editorLoadingDialogTitle}
                message={localization.editorLoc.logoEditorContent.editorLoadingDialogMsg}
        />, false);

        // load visual data for product
        const initUrl: string = this.apiUrl;

        const request: IInitEditorRequest = {
            supplierPrefix: this.state.pPrefix,
            productCode: this.state.pCode,
            productGroupCode: this.state.pGroup,
            productVariantCode: this.state.pVariant,
            logoId: this.state.logoId,
            logoIsOverride: this.state.logoIsOverride,
            productMotiveId: this.state.productMotiveId
        }
        
        let initData: IInitEditorResponse;
        try {
            const response = await Axios.post<IInitEditorResponse>(initUrl, request);
            initData = response.data;
        } catch (error) {
            this.props.appContext.openDialog(<SimpleMessageDialog
                title={localization.editorLoc.logoEditorContent.inicializationFailedTitle}
                message={localization.editorLoc.logoEditorContent.inicializationFailedServerError}
                buttonText={localization.editorLoc.logoEditorContent.closeEditorButton}
                onClick={() => this.props.appContext.closeApp()}
            />, false)
            return;
        }

        DebuggerInstance.log('****Visual data response:****');
        DebuggerInstance.log(initData);

        const brandingTechnologies = initData.productVisualization.brandingTechnologies || [];
        const selectedBrandingTechnology = brandingTechnologies.length > 0 ? brandingTechnologies[0] : null;

        const currentLogo = initData.logoId || this.state.logoId;
        this.setState({
            visualization: initData.productVisualization,
            logoId: currentLogo, // logo may be change by product motive
            logoIsOverride: currentLogo !== this.state.globalLogoId,
            logoIsMotive: initData.logoIsMotive,
            logoMotiveData: initData.logoMotiveData,
            productMotiveId: initData.productMotiveId,
            productId: initData.productId,
            productSupplierId: initData.productSupplierId,
            productTitle: initData.productTitle,
            brandingTechnologies,
            selectedBrandingTechnology,
            editorDataInitialized: true,
            printZoneColors: initData.productVisualization.printZoneColors,
            watermarkVisualization: initData.watermarkVisualization,
            watermarkLogo: initData.watermarkLogo
        });

        this.langUpdateHandle();

        this.props.appContext.raiseTronlogoEvent({
            type: 'OPENEDITOR',
            motiveId: this.state.logoId || undefined,
            productMotiveId: this.state.productMotiveId || undefined,
            sid: this.state.pPrefix,
            spcode: this.state.pCode,
            smcode: this.state.pGroup,
            smcolor: this.state.pVariant,
            pcode: this.state.sCode,
            pname: this.state.sName
        });

        await this.loadCustomEditorButtons();

        this.tryInitEditor();
    }

    public componentWillUnmount() {
        LocHelperInstance.langChanged.removeListener(langChangedName, this.langUpdateHandle);
    }

    /**
     * Check if all resources are loaded and editor can be initialized
     */
    private tryInitEditor(): void {
        DebuggerInstance.log('tryInitEditor called');
        DebuggerInstance.log(this.state);

        // already initialized
        if (this.state.editorInitialized) {
            return;
        }

        // tron editor script is not loaded yet
        if (!this.state.scriptLoaded) {
            return;
        }

        // load of data from server is not completed yet
        if (!this.state.editorDataInitialized) {
            return;
        }

        // product visual data not loaded yet
        if (!this.state.visualization) {
            this.props.appContext.openDialog(<SimpleMessageDialog
                title={localization.editorLoc.logoEditorContent.inicializationFailedTitle}
                message={localization.editorLoc.logoEditorContent.inicializationFailedNoVisualization}
                buttonText={localization.editorLoc.logoEditorContent.closeEditorButton}
                onClick={() => this.props.appContext.closeApp()}
            />, false)
            return;
        }

        DebuggerInstance.log('Starting TronEditor inicialization');
        this.setState({
            editorInitialized: true
        });

        this.initializeTronEditor(this.state.visualization);
    }

    private handleTronEditorError(): void {
        DebuggerInstance.error('scripts/tron-editor.js failed to load');

        this.props.appContext.openDialog(<SimpleMessageDialog
            title={localization.editorLoc.logoEditorContent.inicializationFailedTitle}
            message={localization.editorLoc.logoEditorContent.inicializationFailedServerError}
            buttonText={localization.editorLoc.logoEditorContent.closeEditorButton}
            onClick={() => this.props.appContext.closeApp()}
        />, false);
    }

    private handleTronEditorLoaded(): void {
        DebuggerInstance.log('scripts/tron-editor.js has loaded');

        this.setState({
            scriptLoaded: true
        });

        this.tryInitEditor();
    }

    private async initializeTronEditor(visualization: IProductVisualization) {
        const initParams: ITronEditorParams = {
            editorDivId: 'editor',
            visualizationDivId: 'visualization',
            watermarkSettings: this.state.watermarkVisualization,
            loadLogoImage: async (id, variant) => {
                DebuggerInstance.log("loadLogoImage called: ");
                DebuggerInstance.log(id);
                DebuggerInstance.log(variant);
                const logoVariant: LogoColorVariant = variant || LogoColorVariant.Original
                const productImageUrl: string = this.apiUrl + `logo/${id}?variant=${logoVariant}`;
                try {
                    const response = await Axios.get<ILogoImageResponse>(productImageUrl);

                    const cache = this.state.logoVariantCache;
                    cache[id] = {
                        hasBlackVariant: response.data.hasBlackVariant || false,
                        hasWhiteVariant: response.data.hasWhiteVariant || false
                    };
                    this.setState({
                        logoVariantCache: cache
                    });
    
                    return response.data.logoImageAsBase64;
                } catch (error) {
                    DebuggerInstance.error(error);

                    this.setState({
                        editorError: true
                    });
    
                    this.props.appContext.openDialog(
                        <EditorLoadErrorDialog
                            onClose={() => this.props.appContext.closeApp()}
                        />, false);
                }

                return '';
            },
            selectedCallback: (index: number | undefined) => {
                DebuggerInstance.log("selectedCallback called: " + index);
                if (index !== undefined) {
                    DebuggerInstance.log(this.editor.visualObjects[index]);
                }
                if (this.state.editorError) {
                    // DO NOT CHANGE SELECTED OBJECT WHILE EDITOR IS IN ERROR STATE
                    return;
                }

                this.setState({ selectedObjectId: index });
            },
            printZoneSizesChangedCallback: () => {
                DebuggerInstance.log('printZoneSizesChangedCallback');
                this.debouncePrintZoneSizeChanged(this.updatePrintZoneSize)
            },
            fontSizeCallback: (fontSize) => {
                DebuggerInstance.log('fontSizeCallback');
                DebuggerInstance.log(fontSize);
                this.setState({});
            },
            visualizationCallback: async () => {
                DebuggerInstance.log('***visualizationCallback***');
                await this.updateMotiveColors();
            },
            initEditorCallback: () => {
                DebuggerInstance.log('initEditorCallback');
            },
            getPantoneFromCOLOR(value:string[]): Promise<string> {
                return Promise.resolve('');
            },
            getHexFromPantone(value: string[]): Promise<string> {
                return Promise.resolve('');
            }
        }

        // inicializace zde az potom co je vyrenderovano html
        this.editor = new promotron.TronEditor.Editor(initParams);

        // inicializace editoru
        await this.editor.initEditor();

        // initialize watermark, watermark logo data are optional
        if (this.state.watermarkVisualization && this.state.watermarkVisualization.on) {
            await this.editor.initWatermark(this.state.watermarkLogo);
        }

        // inicializace dat produktu
        const productImage = await this.loadProductImage(visualization.productVisualId.toString())
        await this.editor.initProduct(productImage, visualization.visualData, this.state.printZoneColors as string);

        // nastaveni pocatecni velikosti
        this.editor.setEditorSize(this.editorSize);
        this.editor.setVisualizationSize(this.visualizationSize);

        // inicializace loga nebo motivu
        if (this.state.logoId) {
            let colorVariant = visualization.logoVariant || LogoColorVariant.Original;
            if (this.state.initialColorVariant) {
                colorVariant = this.state.initialColorVariant;
            }

            if (this.state.logoIsMotive && this.state.logoMotiveData) {
                await this.editor.initMotiveData(this.state.logoMotiveData, colorVariant as any);
            } else {
                await this.editor.initMotiveFromLogo(this.state.logoId, colorVariant);
            }
        }

        let selectedTech: IBrandingTechnology | null = null;
        if (this.state.brandingTechnologies.length > 0) {
            if (this.state.presetEffectCode) {
                selectedTech = this.state.brandingTechnologies.find(bt => bt.techCode === this.state.presetEffectCode) || null;
            }
            if (selectedTech === null && visualization.effectCode) {
                selectedTech = this.state.brandingTechnologies.find(bt => bt.techCode === visualization.effectCode) || null;
            }
            if (selectedTech === null) {
                selectedTech = this.state.brandingTechnologies[0];
            }
            
            if (selectedTech) {
                this.applyBrandingTechnology(selectedTech);
            }
        }

        await this.updateMotiveColors();
        this.setState({
            magnetEnabled: this.editor.magnetEnabled,
            editorLoaded: true,
            printData: this.getPrintZoneSizes()
        });        

        if (!this.state.editorError) {
            this.props.appContext.closeDialog();

            if (!this.state.logoId) {
                this.showGallery();
            }
        }
    }

    private async updateMotiveColors() {
        let colors: string[] = await this.editor.getCanvasColors();
        colors = colors.splice(0, maxMotiveColors);

        this.setState({
            canvasColorsCount: colors.length,
            initialLogoColors: colors
        });
    }

    private async loadProductImage(productId: string): Promise<string> {
        const productImageUrl: string = this.apiUrl + `product-visual/${productId}/product-image`;
        const response = await Axios.get<IProductImageResponse>(productImageUrl);

        return response.data.productImagesAsBase64;
    }

    /**
     * creates or updates motive from editor
     * @param addToGallery motive will be shown in gallery
     * @param showOnAll motive is applied to all products
     */
    private async saveMotive(addToGallery: boolean, showOnAll: boolean): Promise<SaveMotiveResult> {
        try {
            const motive = await this.GetMotive();

            let url = this.apiUrl + 'motive';
            // (!(is motive) => is plain logo) or (not show on all and not related to current product)
            const saveAsNew: boolean = !this.state.logoIsMotive || (!showOnAll && this.state.productMotiveId === null);
            if (!saveAsNew) {
                // update existing motive
                url += `/${this.state.logoId}`;
            }
            
            const request: ICreateMotiveRequest = {
                motiveData: motive.motiveData,
                motiveVisualization: motive.motiveVisualization,
                relatedProductId: this.state.productId,
                relatedProductSupplierId: this.state.productSupplierId,
                addToGallery
            };

            if (this.state.selectedBrandingTechnology) {
                request.effectCode = this.state.selectedBrandingTechnology.techCode;
            }

            const response = await Axios.post<ICreateMotiveResponse>(url, request);
            if (response === null || response.data === null) {
                throw new Error('Response is not valid!');
            }

            this.setState({
                logoIsMotive: true,
                logoMotiveData: motive.motiveData,
                logoId: response.data.motiveId,
                logoIsOverride: !showOnAll,
                globalLogoId: showOnAll ? response.data.motiveId : this.state.globalLogoId,
                productMotiveId: response.data.productMotiveId,
            });

            // call product motive callback
            this.props.appContext.raiseTronlogoEvent({
                type: 'MOTIVESAVE',
                motiveId: this.state.logoId as string, // logo should be always set after save
                productMotiveId: response.data.productMotiveId || undefined,
                sid: this.state.pPrefix,
                spcode: this.state.pCode,
                smcode: this.state.pGroup,
                smcolor: this.state.pVariant,
                pcode: this.state.sCode,
                pname: this.state.sName,
                saveandapply: showOnAll,
                savetogallery: addToGallery,
                visualId: this.state.userVisualId,
                printtechnology: this.state.selectedBrandingTechnology ? this.state.selectedBrandingTechnology.techCode : null,
                motivecolorscount: this.state.canvasColorsCount,
                posCode: this.state.printData.posCode,
                posName: this.state.printData.posName,
                posDef: this.state.printData.posDefault,
                techCode: this.state.printData.techCode,
                techName: this.state.printData.techName,
                techDef: this.state.printData.techDefault,
                areaWidth: this.state.printData.areaWidth,
                areaHeight: this.state.printData.areaHeight,
                motiveWidth: this.state.printData.motiveWidth,
                motiveHeight: this.state.printData.motiveHeight,
                maxColors: this.state.printData.maxColors,
            });
        } catch (error) {
            DebuggerInstance.error('saveMotive failed with error:');
            DebuggerInstance.error(JSON.stringify(error));

            let errorMsg = 'Server error!';
            const response = error.response as AxiosResponse<ISimpleResponse>;
            if (response && response.data.message) {
                errorMsg = response.data.message;
            }

            return {
                success: false,
                message: errorMsg
            };
        }

        return { success: true };
    }

    private async GetMotive(): Promise<IMotiveInfo> {
        const motiveData = await this.editor.getMotiveData();
        const motiveBitmaps = await this.editor.getMotiveBitmap();
        
        const result: IMotiveInfo = {
            motiveData,
            motiveVisualization: {
                original: motiveBitmaps[0],
                black: motiveBitmaps[1],
                white: motiveBitmaps[2]
            }
        }
        
        DebuggerInstance.log('GetMotive - result');
        DebuggerInstance.log(result);

        return result;
    }

    private closeEditorAndApplyMotive() {        
        if (this.state.logoIsOverride) {
            if(this.state.globalLogoId) {
                DebuggerInstance.log(`applying motive ${this.state.globalLogoId}`);
                this.props.appContext.onLogochanged(this.state.globalLogoId);
            }
        } else {
            if(this.state.logoId) {
                DebuggerInstance.log(`applying motive ${this.state.logoId}`);
                this.props.appContext.onLogochanged(this.state.logoId);
            }
        }
        this.props.appContext.closeApp(closeDelay);
    }

    //#region GALLERY

    private showGallery() {
        DebuggerInstance.log('showing gallery....');

        const gallery = this.getGallery()

        this.props.appContext.openDialog(
            <div className="editor-gallery-popup-dialog-root">
                <div className="title primary-style">
                    <h3>{localization.editorLoc.logoEditorContent.galleryDialogTitle}</h3>
                </div>
                <div className="content-container h-100">
                    {gallery}                 
                </div>
            </div>
        );
    }

    private handleGalleryBack() {
        this.props.appContext.closeDialog();
    }

    private async handleGallerySelected(logoId: string) {
        DebuggerInstance.log('handleGallerySelected');
        DebuggerInstance.log(logoId);        

        this.props.appContext.closeDialog();

        await this.editor.createImage(logoId, LogoColorVariant.Original);
        await this.updateMotiveColors();
    }

    private getGallery(): JSX.Element {
        return (
            <LogoEditorGallery
                onUploadLogo={() => this.showUpload()}
                onBack={() => this.handleGalleryBack()} 
                onImageSelected={(logoId) => this.handleGallerySelected(logoId)} />)
    }

    //#endregion

    //#region UPLOAD

    private showUpload() {
        DebuggerInstance.log('showing upload....');

        this.props.appContext.openDialog(
            <div className="editor-upload-popup-dialog-root">
                <div className="title primary-style">
                    <h3>{localization.uploadLoc.title}</h3>
                </div>
                <div className="content-container h-100">
                    <LogoUploadEditor
                        onUploadSuccess={(id) => this.handleUploadSuccess(id)}
                        onUploadFail={() => this.handleUploadFail()}
                    />
                </div>
            </div>
        );
    }

    private handleUploadSuccess(logoId: string) {
        this.showGallery();
    }

    private handleUploadFail() {
        this.showGallery();
    }

    //#endregion

    private editorHintDialog(): void {
        // Link to PromoTron PDF user guide
        window.open(localization.commonLoc.controlsLinkValue);
    }

    private handleEditorResize(width: number, height: number) {
        width = Math.trunc(width) - 2;
        height = Math.trunc(height) - 2;

        this.editorSize = { width, height };

        if (this.state.editorLoaded) {
            DebuggerInstance.log('handleEditorResize');
            DebuggerInstance.log(this.editorSize);
            this.editor.setEditorSize(this.editorSize);
            this.setState({});
        }
    }

    private handleVisualizationResize(width: number, height: number): void {
        width = Math.trunc(width) - 1;
        height = Math.trunc(height) - 1;

        this.visualizationSize = { width, height };

        if (this.state.editorLoaded) {
            DebuggerInstance.log('handleVisualizationResize');
            DebuggerInstance.log(this.visualizationSize);
            this.editor.setVisualizationSize(this.visualizationSize);
            this.setState({});
        }
    }

    private getPrintZoneSizes(): IPrintData {
        return this.editor.getPrintData();
    }

    private updatePrintZoneSize(): void {
        this.setState({
            printData: this.getPrintZoneSizes()
        });
    }

    private async handleAddShape(shapeType: ShapeType) {
        const id = await this.editor.createShape(shapeType);

        let topColor: string;
        if (this.state.initialLogoColors && this.state.initialLogoColors.length > 0) {
            topColor = this.state.initialLogoColors[0];
        } else {
            topColor = '#000';
        }

        if (shapeType === ShapeType.Text) {
            this.editor.setShapeProps(
                id, 
                { 
                    Text: localization.editorLoc.logoEditorContent.textDefault,
                    FillColor: topColor
                })
        }

        if (shapeType === ShapeType.Rect) {
            this.editor.setShapeProps(
                id, 
                { 
                    StrokeColor: topColor
                })
        }

        if (shapeType === ShapeType.Line) {
            this.editor.setShapeProps(
                id, 
                { 
                    StrokeColor: topColor
                })
        }

        await this.updateMotiveColors();
    }

    private async handleSendPreview(email: string, saveMotive: any) {
        if (saveMotive) {
            // 1 - save motive
            this.props.appContext.openDialog(<SavingMotiveDialog />, false);
            const saveResult = await this.saveMotive(true, true);
            if (!saveResult.success) {
                this.props.appContext.openDialog(
                    <SaveMotiveErrorDialog
                        errorMessage={saveResult.message}
                        onClose={() => this.props.appContext.closeDialog()}
                    />)
                return;
            }
        }

        // 2 - send proofreading email
        this.props.appContext.openDialog(<GeneratingPdfDialog />, false);
        const productData = await this.getProductVisualizationData();
        const request: IPdfPreviewRequest = {
            ...productData,
            customerLanguage: LocHelperInstance.getCurrentLanguage() || "en",
            isFinalConfirmation: true,
            email,
            userVisualId: this.state.userVisualId || null
        };

        try {
            await Axios.post<IPdfPreviewRequest>(this.apiUrl + 'send-visualization', request);

            this.props.appContext.raiseTronlogoEvent({
                type: 'VISUALSENTDONE',
                sid: this.state.pPrefix,
                spcode: this.state.pCode,
                smcode: this.state.pGroup,
                smcolor: this.state.pVariant,
                pcode: this.state.sCode,
                pname: this.state.sName,
                email,                
                motiveId: this.state.logoId || undefined,
                productMotiveId: this.state.productMotiveId || undefined,
                visualId: this.state.userVisualId,
                motivecolorscount: this.state.canvasColorsCount,
                posCode: this.state.printData.posCode,
                posName: this.state.printData.posName,
                posDef: this.state.printData.posDefault,
                techCode: this.state.printData.techCode,
                techName: this.state.printData.techName,
                techDef: this.state.printData.techDefault,
                areaWidth: this.state.printData.areaWidth,
                areaHeight: this.state.printData.areaHeight,
                motiveWidth: this.state.printData.motiveWidth,
                motiveHeight: this.state.printData.motiveHeight,
                maxColors: this.state.printData.maxColors,
            });

            this.props.appContext.openDialog(
                <PreviewSendSuccessDialog onClick={() => {
                    this.closeEditorAndApplyMotive();}
                }/>)
        } catch (error) {
            DebuggerInstance.error('handleSendPreview ended with error:');
            DebuggerInstance.error(error);

            let errorMsg = 'Server error!';
            const response = error.response as AxiosResponse<ISimpleResponse>;
            if (response && response.data.message) {
                errorMsg = response.data.message;
            }

            this.props.appContext.raiseTronlogoEvent({
                type: 'VISUALSENTFAIL',
                sid: this.state.pPrefix,
                spcode: this.state.pCode,
                smcode: this.state.pGroup,
                smcolor: this.state.pVariant,
                pcode: this.state.sCode,
                pname: this.state.sName,
                email,                
                motiveId: this.state.logoId || undefined,
                productMotiveId: this.state.productMotiveId || undefined,
                visualId: this.state.userVisualId ? this.state.userVisualId.toString() : undefined,
                errormsg: errorMsg
            });

            this.props.appContext.openDialog(
                <SendProofreadingErrorDialog
                    errorMessage={errorMsg}
                    onClose={() => this.props.appContext.closeDialog()}
                />);
        }
    }

    private async getProductVisualizationData(): Promise<IProductVisualizationData> {
        if (!this.state.visualization) {
            throw new Error("Missing visualizations!");
        }

        const printAreaSizes = this.editor.getPrintData();
        const printWidth: number = printAreaSizes.areaWidth;
        const printHeight: number = printAreaSizes.areaHeight;
        const motiveWidth: number = printAreaSizes.motiveWidth;
        const motiveHeight: number = printAreaSizes.motiveHeight;

        const motiveVisualization = await this.editor.getMotiveBitmap();
        const motiveData = await this.editor.getMotiveData();

        let effect: string | null = null;
        if (this.state.selectedBrandingTechnology) {
            effect = this.state.selectedBrandingTechnology.techCode;
        }

        const data: IProductVisualizationData = {
            supplierPrefix: this.state.pPrefix,
            productCode: this.state.pCode,
            productGroup: this.state.pGroup,
            productVariant: this.state.pVariant,
            shopProductCode: this.state.sCode,
            shopProductName: this.state.sName,
            productVisualId: this.state.visualization.productVisualId,
            logoVisualization: motiveVisualization[0],
            printAreaWidth: printWidth,
            printAreaHeight: printHeight,
            motiveAreaWidth: motiveWidth,
            motiveAreaHeight: motiveHeight,
            motiveJson: motiveData,
            effectCode: effect,
            printPosition: this.state.printData.posName,
            printTechnology: this.state.printData.techName,
            motiveColors: this.state.initialLogoColors.length
        }

        return data;
    }

    /**
     * Opens dialog to remove product-motive relation
     */
    private async handleClearProductMotive(): Promise<void> {
        if (!this.state.productMotiveId) {
            return;   
        }

        this.props.appContext.openDialog(
            <SimpleChoiceDialog
                title={localization.editorLoc.logoEditorContent.clearProductMotiveTitleDialog}
                content={localization.editorLoc.logoEditorContent.clearProductMotiveContentDialog}
                leftChoiceText={localization.commonLoc.close}
                leftChoiceClick={() => this.props.appContext.closeDialog()}
                rightChoiceText={localization.commonLoc.accept}
                rightChoiceClick={async () => {
                    await this.clearProductMotive();
                    this.closeEditorAndApplyMotive();
                }}
            />,
            true)
    }

    private async clearProductMotive() {
        if (!this.state.productMotiveId) {
            return;   
        }

        try {
            const url = this.apiUrl + `product-motive/${this.state.productMotiveId}`;
            await Axios.delete(url);
            this.setState({
                productMotiveId: null,
                logoId: this.state.globalLogoId
            });
        } catch (error) {
            return;
        }
    }

    //#region Visualization Buttons

    private async loadCustomEditorButtons() {
        try {
            const apiKey = this.props.appContext.getApiKey();
            const currLang = localization.getLanguage();
            const response = await Axios.get<ICustomEditorButtonsSettings>(`/api/${apiKey}/customization/custom-editor-buttons/${currLang}`);
            this.setState({customEditorButtonsSettings: response.data})
        } catch (e) {
            DebuggerInstance.error(e);
        }
    }

    private getVisualizationButtons(): React.ReactNode {
        if (this.state.customEditorButtonsSettings && this.state.customEditorButtonsSettings.active) {
            return this.getCustomVisualizationButtons(this.state.customEditorButtonsSettings);
        }

        return this.getDefaultVisualizationButtons()
    }

    private getCustomVisualizationButtons(settings: ICustomEditorButtonsSettings) {
        return (<CustomLogoEditorPreviewControls
            settings={settings}
            executeBtnFunction={(f) => this.executeBtnFunction(f)}
        />);
    }

    private getDefaultVisualizationButtons(): React.ReactNode {
        return (
            <LogoEditorPreviewControls
                onSaveClick={() => this.executeBtnFunction(EditorButtonsFunctions.SaveSelection)}
                onContinueClick={() => this.executeBtnFunction(EditorButtonsFunctions.PdfVisualization)}
            />)
    }

    //#endregion

    //#region Visualization Buttons Functions

    private async executeBtnFunction(f: EditorButtonsFunctions) {
        switch (f) {
            case EditorButtonsFunctions.DownloadMotive:
                await this.downloadMotive();
                return;
            case EditorButtonsFunctions.DownloadVisualization:
                this.downloadVisualization();
                return;
            case EditorButtonsFunctions.PdfVisualization:
                await this.showProofreading();
                return;
            case EditorButtonsFunctions.QuickSave:
                await this.quickSave();
                return;
            case EditorButtonsFunctions.SaveSelection:
                await this.saveSelection();
                return;
        }
    }

    /**
     * Download motive as image
     */
    private async downloadMotive() {
        const motive = await this.editor.getMotiveBitmap();
        if (!motive) { return; }
        if (motive.length <= 0) { return; }
        if (!motive[0]) { return; }

        const downloadLink = document.createElement('a');
        downloadLink.setAttribute('href', motive[0]);
        downloadLink.setAttribute('download', 'motive.png');
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
    }

    /**
     * Download product visualization as image
     */
    private downloadVisualization() {
        const visualizationCanvas = document.getElementById('promotron-visualization-canvas') as HTMLCanvasElement;
        const visualizationPng = visualizationCanvas.toDataURL("image/png");
        const downloadLink = document.createElement('a');
        downloadLink.setAttribute('href', visualizationPng);
        downloadLink.setAttribute('download', 'visualization.png');
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
    }

    /**
     * Generating PDF with visualization for preview
     */
    private async showProofreading(): Promise<void> {
        this.props.appContext.openDialog(<GeneratingPdfDialog />, false);

        const productData = await this.getProductVisualizationData();
        const request: IProofreadingRequest = {
            ...productData,
            customerLanguage: LocHelperInstance.getCurrentLanguage() || "en"
        }

        try {
            const response = await Axios.post<IProofreadingResponse>(this.apiUrl + 'proofreading', request);
            this.setState({
                userVisualId: response.data.userVisualId
            });

            this.props.appContext.raiseTronlogoEvent({
                type: 'VISUALCREATEDONE',
                sid: this.state.pPrefix,
                spcode: this.state.pCode,
                smcode: this.state.pGroup,
                smcolor: this.state.pVariant,
                pcode: this.state.sCode,
                pname: this.state.sName,
                motiveId: this.state.logoId || undefined,
                productMotiveId: this.state.productMotiveId || undefined,
                visualId: response.data.userVisualId,
                printtechnology: productData.effectCode,
                motivecolorscount: this.state.canvasColorsCount,
                posCode: this.state.printData.posCode,
                posName: this.state.printData.posName,
                posDef: this.state.printData.posDefault,
                techCode: this.state.printData.techCode,
                techName: this.state.printData.techName,
                techDef: this.state.printData.techDefault,
                areaWidth: this.state.printData.areaWidth,
                areaHeight: this.state.printData.areaHeight,
                motiveWidth: this.state.printData.motiveWidth,
                motiveHeight: this.state.printData.motiveHeight,
                maxColors: this.state.printData.maxColors,
            });

            this.props.appContext.openDialog(
                <Proofreading
                    onClose={() => this.props.appContext.closeDialog()}
                    onSendConfirm={(email, saveMotive) => this.handleSendPreview(email, saveMotive)}
                    previewImg={response.data.previewImage}
                />);
        } catch (error) {
            DebuggerInstance.error('showProofreading ended with error:');
            DebuggerInstance.error(JSON.stringify(error));

            let errorMsg = 'Server error!';
            const response = error.response as AxiosResponse<ISimpleResponse>;
            DebuggerInstance.error('response');
            DebuggerInstance.error(JSON.stringify(response));

            if (response && response.data.message) {
                errorMsg = response.data.message;
            }

            this.props.appContext.raiseTronlogoEvent({
                type: 'VISUALCREATEFAIL',
                sid: this.state.pPrefix,
                spcode: this.state.pCode,
                smcode: this.state.pGroup,
                smcolor: this.state.pVariant,
                pcode: this.state.sCode,
                pname: this.state.sName,
                motiveId: this.state.logoId || undefined,
                productMotiveId: this.state.productMotiveId || undefined,
                errormsg: errorMsg
            })

            this.props.appContext.openDialog(
                <SendProofreadingErrorDialog
                    errorMessage={errorMsg}
                    onClose={() => this.props.appContext.closeDialog()}
                />)
        }
    }

    /**
     * Save motive with selection dialog
     */
    private async saveSelection() {
        this.props.appContext.openDialog(
            <SaveMotiveDialog onSave={async (addToGallery, showOnAll) => {
            this.props.appContext.openDialog(<SavingMotiveDialog />, false);
            const saveResult = await this.saveMotive(addToGallery, showOnAll);
            if (saveResult.success) {
                this.closeEditorAndApplyMotive();
            } else {
                this.props.appContext.openDialog(
                    <SaveMotiveErrorDialog
                        errorMessage={saveResult.message}
                        onClose={() => this.props.appContext.closeDialog()}
                    />)
            }
        }}/>, true);
    }

    private async quickSave() {
        const saveResult = await this.saveMotive(true, true);
        if (saveResult.success) {
            this.closeEditorAndApplyMotive();
        } else {
            this.props.appContext.openDialog(
                <SaveMotiveErrorDialog
                    errorMessage={saveResult.message}
                    onClose={() => this.props.appContext.closeDialog()}
                />)
        }
    }

    //#endregion

    //#region Branding

    private applyBrandingTechnology(technology: IBrandingTechnology) {
        this.setState({
            selectedBrandingTechnology: technology
        });

        const visualParam: IVisualizationModeParam = {
            printType: this.GetPrintType(technology.effectType),
            shadowAmount: technology.shadowAmount || 0,
            shadowOffsetX: technology.shadowOffsetX || 0,
            shadowOffsetY: technology.shadowOffsetY || 0,
            shadowBlur: technology.shadowBlur || 0,
            shadowColor: technology.shadowColor || undefined,
            lightAmount: technology.lightAmount || 0,
            lightOffsetX: technology.lightOffsetX || 0,
            lightOffsetY: technology.lightOffsetY || 0,
            lightBlur: technology.lightBlur || 0,
            lightColor: technology.lightColor || undefined,
            isWood: technology.isWood,
            surfaceDetail: technology.surfaceDetail || 0,
            surfaceColor: technology.surfaceColor || undefined,
            maskColorWood: technology.maskColorWood || undefined,
            maskColorEmboss: technology.maskColorEmboss || undefined,
        }

        this.editor.setVisualizationModeEffect(visualParam);
    }

    private GetPrintType(effectType: string): PrintEffectType {
        switch (effectType) {
            case "EMBOSS":
                return PrintEffectType.Emboss;
            case "LASER":
                return PrintEffectType.Laser;
            case "EMBROIDERY":
                return PrintEffectType.Embroidery;
            default:
                return PrintEffectType.None;
        }
    }

    //#endregion

    public render() {
        DebuggerInstance.log('render');
        DebuggerInstance.log(this.state);

        return (
            <div className="tron-editor-root">
                <div className="tron-editor">
                    <div className="tron-editor-panels">
                        <div className="editor-panel">
                            <div className="editor-parts">
                                <div className="editor-print-area">
                                    <LogoEditorPosition
                                        colorsInMotive={this.state.canvasColorsCount}
                                        printData={this.state.printData}
                                        selectedBrandingTechnology={this.state.selectedBrandingTechnology}
                                        />
                                </div>
                                <div className="editor-main">
                                    <div className="editor-main-wrapper">
                                        <div className="editor-controls">
                                            <LogoEditorImageControls
                                                magnetEnabled={this.state.magnetEnabled}
                                                onAddLogo={() => this.showGallery()}
                                                onAddLine={() => this.handleAddShape(ShapeType.Line)}
                                                onAddRectangle={() => this.handleAddShape(ShapeType.Rect)}
                                                onAddText={() => this.handleAddShape(ShapeType.Text)}
                                                onUndo={() => this.editor.undo()}
                                                onRedo={() => this.editor.redo()}
                                                onSaveToGallery={() => this.saveSelection()}
                                                onQuestionMarkClick={() => this.editorHintDialog()}
                                                onMagnetClick={() => 
                                                    { 
                                                        this.editor.magnetEnabled = !this.editor.magnetEnabled; 
                                                        this.setState({magnetEnabled: this.editor.magnetEnabled})
                                                    }}
                                            />
                                        </div>
                                        <div className="editor-container">
                                            <LogoEditorImage onEditorResize={(w,h) => this.handleEditorResize(w,h)} />
                                        </div> 
                                    </div>
                                </div>
                                <div className="editor-shape-properties">
                                    <LogoEditorObjectSettings
                                        editor={this.editor}
                                        selectedObjectId={this.state.selectedObjectId}
                                        logoVariantsCache={this.state.logoVariantCache}
                                        initialLogoColors={this.state.initialLogoColors}
                                    />
                                </div>
                                <div className="editor-control-tip">
                                    <p><Markdown text={localization.editorLoc.logoEditorContent.controlHints.editorHintLabel} /></p>
                                </div>
                            </div>
                        </div>
                        <div className="visualization-panel">
                            <div className="visualization-panel-parts">
                                <div className="visualization-panel-preview-part">
                                    <LogoEditorPreview 
                                        showClearProductMotive={this.state.productMotiveId !== null}
                                        onClearProductMotive={() => this.handleClearProductMotive()}
                                        onVisualizationResize={(w,h) => this.handleVisualizationResize(w,h)}
                                        />
                                </div>
                                <div className="visualization-panel-controls-part">
                                    {this.getVisualizationButtons()}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <Script url="/scripts/tron-editor-chunk.js" /*url="/tron-editor-chunk.js"*/
                    onLoad={() => this.handleTronEditorLoaded()}
                    onError={() => this.handleTronEditorError()}
                />
                <ReactTooltip effect={'solid'} />
            </div>
        );
    }
}

export const LogoEditorContainerContext = withAppContext(LogoEditorContainer);



// WEBPACK FOOTER //
// ./src/App/logo-app/logo-editor/logo-editor-container.component.tsx